import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TelemetryLogs from '../components/TelemetryLogs';
import ErrorLogPanel from '../components/ErrorLogPanel';
import FlightReportModal from '../components/FlightReportModal';
import { API_URL, apiFetch } from '../config/api';

/* --- Clean SVG Tactical Icons --- */
const IconDrone = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const IconRefresh = ({ size = 14, color = 'currentColor', spinning = false }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2.2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ animation: spinning ? 'utms-spin 0.8s linear infinite' : 'none', flexShrink: 0 }}
    >
        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
    </svg>
);

const IconDownload = ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const IconDocument = ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
    </svg>
);

const IconChevron = ({ size = 14, color = 'currentColor', expanded = false }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const IconSearch = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const IconCalendar = ({ size = 12, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const IconClock = ({ size = 12, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);


const IconActivity = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
);

const IconAlert = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const IconHistory = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 14 14" />
        <path d="M3.05 11a9 9 0 0 1 .5-2m.5-2a9 9 0 0 1 14.9-2.9L21 6" />
    </svg>
);

/**
 * Logs Page - Live telemetry stream, Error Logs, and Historical Flight Logs
 */
function LogsPage({ telemetry, selectedDroneId: propSelectedDroneId, dronesTelemetry = {} }) {
    const [tab, setTab] = useState('live'); // 'live' | 'errors' | 'history'
    const [flightLogs, setFlightLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedLogId, setExpandedLogId] = useState(null);
    const [expandedLog, setExpandedLog] = useState(null);
    const [reportLog, setReportLog] = useState(null);
    const [reportLoadingId, setReportLoadingId] = useState(null);
    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
    
    // Filtering states
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'completed' | 'recording' | 'aborted'
    const [overrideDroneFilter, setOverrideDroneFilter] = useState(false);

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isCompact = viewportWidth <= 1180;
    const isPhone = viewportWidth <= 768;

    const loadFlightLogs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_URL}/flight-logs`);
            if (res.ok) {
                const data = await res.json();
                if (data.success) setFlightLogs(data.logs || []);
            }
        } catch (err) {
            console.error('Failed to load flight logs:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (tab === 'history') loadFlightLogs();
    }, [tab, loadFlightLogs]);

    const handleExpand = async (logId) => {
        if (expandedLogId === logId) {
            setExpandedLogId(null);
            setExpandedLog(null);
            return;
        }
        try {
            const res = await apiFetch(`${API_URL}/flight-logs/${logId}`);
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setExpandedLogId(logId);
                    setExpandedLog(data.log);
                }
            }
        } catch (err) {
            console.error('Failed to load log detail:', err);
        }
    };

    const handleExport = async (logId, e) => {
        e.stopPropagation();
        try {
            const res = await apiFetch(`${API_URL}/flight-logs/${logId}/export`);
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `flight_${logId}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error('Export failed:', err);
        }
    };

    const handleOpenReport = async (logId, e) => {
        if (e) e.stopPropagation();
        if (expandedLogId === logId && expandedLog && expandedLog.snapshots && expandedLog.snapshots.length > 0) {
            setReportLog(expandedLog);
            return;
        }
        setReportLoadingId(logId);
        try {
            const res = await apiFetch(`${API_URL}/flight-logs/${logId}`);
            if (res.ok) {
                const data = await res.json();
                if (data.success && data.log) {
                    setReportLog(data.log);
                }
            }
        } catch (err) {
            console.error('Failed to load log for report:', err);
        } finally {
            setReportLoadingId(null);
        }
    };

    const formatDuration = (start, end) => {
        if (!start) return '—';
        const ms = (end ? new Date(end) : new Date()) - new Date(start);
        const s = Math.floor(ms / 1000);
        if (s < 60) return `${s}s`;
        const m = Math.floor(s / 60);
        const rs = s % 60;
        if (m < 60) return `${m}m ${rs}s`;
        const h = Math.floor(m / 60);
        return `${h}h ${m % 60}m`;
    };

    const formatDistance = (m) => {
        if (!m || m < 1) return '0 m';
        if (m < 1000) return `${Math.round(m)} m`;
        return `${(m / 1000).toFixed(2)} km`;
    };

    const effectiveSelectedDroneId = overrideDroneFilter ? null : propSelectedDroneId;

    // Filter logs
    const filteredLogs = useMemo(() => {
        let logs = flightLogs || [];
        
        // Drone filter
        if (effectiveSelectedDroneId) {
            logs = logs.filter(log => log && (log.droneId === effectiveSelectedDroneId || log.droneName === effectiveSelectedDroneId));
        }

        // Status filter
        if (statusFilter !== 'all') {
            logs = logs.filter(log => log.status === statusFilter);
        }

        // Search query
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            logs = logs.filter(log => {
                const droneName = (log.droneName || '').toLowerCase();
                const droneId = (log.droneId || '').toLowerCase();
                const dateStr = log.startedAt ? new Date(log.startedAt).toLocaleString().toLowerCase() : '';
                return droneName.includes(q) || droneId.includes(q) || dateStr.includes(q);
            });
        }

        return logs;
    }, [flightLogs, effectiveSelectedDroneId, statusFilter, searchQuery]);

    // Status counts for filter chips
    const statusCounts = useMemo(() => {
        const counts = { all: (flightLogs || []).length, completed: 0, recording: 0, aborted: 0 };
        (flightLogs || []).forEach(log => {
            if (counts[log.status] !== undefined) counts[log.status]++;
        });
        return counts;
    }, [flightLogs]);

    const activeTelemetry = propSelectedDroneId && dronesTelemetry[propSelectedDroneId]
        ? dronesTelemetry[propSelectedDroneId]
        : telemetry;

    return (
        <div className="utms-logs-page" style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: isPhone ? '14px' : '20px', gap: isPhone ? '14px' : '18px', background: 'var(--bgPrimary)', boxSizing: 'border-box' }}>
            {/* Scoped CSS resets & component styles to eliminate global button bleed and harsh cyan wireframes */}
            <style>{`
                @keyframes utms-spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes utms-pulse-dot {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.4; transform: scale(0.85); }
                }
                .utms-logs-page button {
                    width: auto !important;
                    margin: 0 !important;
                    text-transform: none !important;
                    letter-spacing: normal !important;
                    font-weight: 500;
                    box-sizing: border-box;
                }
                .utms-tab-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 8px 18px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.18s ease;
                    border: 1px solid transparent;
                }
                .utms-tab-btn.active {
                    background: color-mix(in srgb, var(--accent) 12%, transparent);
                    color: var(--accent);
                    border-color: color-mix(in srgb, var(--accent) 28%, transparent);
                    box-shadow: 0 0 12px -2px color-mix(in srgb, var(--accent) 20%, transparent);
                }
                .utms-tab-btn:not(.active) {
                    background: transparent;
                    color: var(--textSecondary);
                }
                .utms-tab-btn:not(.active):hover {
                    color: var(--textPrimary);
                    background: color-mix(in srgb, var(--textPrimary) 4%, transparent);
                }
                .utms-flight-row {
                    background: rgba(24, 26, 29, 0.65);
                    border: 1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent);
                    border-radius: 10px;
                    margin-bottom: 8px;
                    cursor: pointer;
                    overflow: hidden;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                }
                .utms-flight-row:hover {
                    background: rgba(30, 34, 39, 0.9);
                    border-color: color-mix(in srgb, var(--accent) 32%, transparent);
                    box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.55);
                    transform: translateY(-1px);
                }
                .utms-flight-row.expanded {
                    border-color: color-mix(in srgb, var(--accent) 40%, transparent);
                    background: rgba(27, 30, 35, 0.95);
                    box-shadow: 0 8px 24px -6px rgba(0, 0, 0, 0.65);
                }
                .utms-btn-action {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 11.5px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    white-space: nowrap;
                    line-height: 1;
                }
                .utms-btn-csv {
                    background: color-mix(in srgb, var(--textPrimary) 4%, transparent);
                    color: var(--textSecondary);
                    border: 1px solid color-mix(in srgb, var(--textPrimary) 10%, transparent);
                }
                .utms-btn-csv:hover {
                    background: color-mix(in srgb, var(--textPrimary) 9%, transparent);
                    border-color: color-mix(in srgb, var(--textPrimary) 25%, transparent);
                    color: var(--textPrimary);
                }
                .utms-btn-report {
                    background: color-mix(in srgb, var(--accent) 8%, transparent);
                    color: var(--accent);
                    border: 1px solid color-mix(in srgb, var(--accent) 25%, transparent);
                }
                .utms-btn-report:hover:not(:disabled) {
                    background: color-mix(in srgb, var(--accent) 16%, transparent);
                    border-color: color-mix(in srgb, var(--accent) 45%, transparent);
                    color: #fff;
                    box-shadow: 0 0 12px -2px color-mix(in srgb, var(--accent) 30%, transparent);
                }
                .utms-btn-report:disabled {
                    opacity: 0.6;
                    cursor: wait;
                }
                .utms-toolbar-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 7px 14px;
                    background: color-mix(in srgb, var(--textPrimary) 5%, transparent);
                    border: 1px solid color-mix(in srgb, var(--textPrimary) 10%, transparent);
                    border-radius: 7px;
                    color: var(--textPrimary);
                    font-size: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.18s ease;
                }
                .utms-toolbar-btn:hover:not(:disabled) {
                    background: color-mix(in srgb, var(--accent) 10%, transparent);
                    border-color: color-mix(in srgb, var(--accent) 35%, transparent);
                    color: var(--accent);
                }
                .utms-filter-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    border: 1px solid transparent;
                }
                .utms-filter-chip.active {
                    background: color-mix(in srgb, var(--accent) 12%, transparent);
                    border-color: color-mix(in srgb, var(--accent) 30%, transparent);
                    color: var(--accent);
                }
                .utms-filter-chip:not(.active) {
                    background: color-mix(in srgb, var(--textPrimary) 3%, transparent);
                    border-color: color-mix(in srgb, var(--textPrimary) 7%, transparent);
                    color: var(--textMuted);
                }
                .utms-filter-chip:not(.active):hover {
                    color: var(--textSecondary);
                    border-color: color-mix(in srgb, var(--textPrimary) 15%, transparent);
                }
            `}</style>

            {/* Page Header */}
            <div style={{ display: 'flex', flexDirection: isPhone ? 'column' : 'row', justifyContent: 'space-between', alignItems: isPhone ? 'stretch' : 'center', gap: '14px', flexShrink: 0 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <h2 style={{ margin: 0, fontSize: isPhone ? '24px' : '26px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--textPrimary)' }}>
                            System Logs & Telemetry
                        </h2>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                            border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)',
                            color: '#34d399',
                        }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', animation: 'utms-pulse-dot 1.8s ease-in-out infinite' }} />
                            Live GCS
                        </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--textMuted)', marginTop: '4px' }}>
                        Real-time telemetry stream, diagnostic system logs, and mission flight records
                    </div>
                </div>

                {/* Tactical Segmented Navigation Tabs */}
                <div style={{
                    display: 'inline-flex',
                    gap: '4px',
                    background: 'rgba(18, 20, 23, 0.75)',
                    padding: '4px',
                    borderRadius: '10px',
                    border: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
                    width: isPhone ? '100%' : 'fit-content',
                }}>
                    {[
                        ['live', 'Telemetry Stream', <IconActivity size={14} />],
                        ['errors', 'System Errors', <IconAlert size={14} />],
                        ['history', 'Flight History', <IconHistory size={14} />],
                    ].map(([key, label, icon]) => {
                        const active = tab === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setTab(key)}
                                className={`utms-tab-btn ${active ? 'active' : ''}`}
                                style={{ flex: isPhone ? 1 : undefined, justifyContent: 'center' }}
                            >
                                {icon}
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Framed Main Content Box */}
            <div style={{
                flex: 1,
                minHeight: 0,
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: 'rgba(17, 19, 22, 0.75)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
                boxShadow: '0 12px 36px -10px rgba(0, 0, 0, 0.65)',
            }}>
                {tab === 'live' && (
                    <div style={{ height: '100%' }}>
                        <TelemetryLogs telemetry={activeTelemetry} droneId={propSelectedDroneId} />
                    </div>
                )}

                {tab === 'errors' && (
                    <div style={{ height: '100%' }}>
                        <ErrorLogPanel />
                    </div>
                )}

                {tab === 'history' && (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {/* Flight History Control Header */}
                        <div style={{
                            padding: isPhone ? '12px 14px' : '14px 18px',
                            background: 'rgba(24, 26, 30, 0.85)',
                            borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 7%, transparent)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            flexShrink: 0,
                        }}>
                            {/* Top row: Title, Search, Refresh */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--textPrimary)', letterSpacing: '0.02em' }}>
                                        Recorded Missions
                                    </span>
                                    <span style={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: 'var(--textMuted)',
                                        background: 'color-mix(in srgb, var(--textPrimary) 4%, transparent)',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    }}>
                                        {filteredLogs.length} of {flightLogs.length}
                                    </span>

                                    {/* Selected Drone Filter Chip */}
                                    {propSelectedDroneId && (
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            background: overrideDroneFilter ? 'color-mix(in srgb, var(--textPrimary) 4%, transparent)' : 'color-mix(in srgb, var(--accent) 10%, transparent)',
                                            border: `1px solid ${overrideDroneFilter ? 'color-mix(in srgb, var(--textPrimary) 10%, transparent)' : 'color-mix(in srgb, var(--accent) 25%, transparent)'}`,
                                            fontSize: '11px',
                                            color: overrideDroneFilter ? 'var(--textMuted)' : 'var(--accent)',
                                        }}>
                                            <span>Focus: {propSelectedDroneId}</span>
                                            <button
                                                type="button"
                                                onClick={() => setOverrideDroneFilter(!overrideDroneFilter)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: 'inherit',
                                                    padding: '0 2px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    lineHeight: 1,
                                                }}
                                                title={overrideDroneFilter ? "Re-apply drone filter" : "Show all drones"}
                                            >
                                                {overrideDroneFilter ? '↺' : '✕'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isPhone ? 1 : 'none', justifyContent: 'flex-end' }}>
                                    {/* Search Input */}
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: 'rgba(16, 17, 20, 0.8)',
                                        border: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
                                        borderRadius: '7px',
                                        padding: '5px 10px',
                                        minWidth: isPhone ? '140px' : '220px',
                                        flex: isPhone ? 1 : 'none',
                                    }}>
                                        <IconSearch size={13} color="var(--textMuted)" />
                                        <input
                                            type="text"
                                            placeholder="Search logs..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                outline: 'none',
                                                color: 'var(--textPrimary)',
                                                fontSize: '12px',
                                                width: '100%',
                                                padding: 0,
                                                margin: 0,
                                            }}
                                        />
                                        {searchQuery && (
                                            <button
                                                type="button"
                                                onClick={() => setSearchQuery('')}
                                                style={{ background: 'transparent', border: 'none', color: 'var(--textMuted)', cursor: 'pointer', padding: 0, fontSize: '13px' }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>

                                    {/* Sleek Refresh Button */}
                                    <button
                                        type="button"
                                        onClick={loadFlightLogs}
                                        disabled={loading}
                                        className="utms-toolbar-btn"
                                        title="Fetch latest flight logs"
                                    >
                                        <IconRefresh size={13} color={loading ? 'var(--accent)' : 'currentColor'} spinning={loading} />
                                        <span>{loading ? 'Syncing...' : 'Refresh'}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Status Filter Chips */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '11px', color: 'var(--textMuted)', marginRight: '4px' }}>Filter:</span>
                                {[
                                    ['all', 'All Flights', statusCounts.all],
                                    ['completed', 'Completed', statusCounts.completed],
                                    ['recording', 'Recording', statusCounts.recording],
                                    ['aborted', 'Aborted', statusCounts.aborted],
                                ].map(([val, label, count]) => (
                                    <button
                                        key={val}
                                        type="button"
                                        onClick={() => setStatusFilter(val)}
                                        className={`utms-filter-chip ${statusFilter === val ? 'active' : ''}`}
                                    >
                                        {val === 'recording' && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--warning)', animation: 'utms-pulse-dot 1.5s infinite' }} />}
                                        {label}
                                        <span style={{ opacity: 0.7, fontSize: '10px' }}>({count})</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Logs Table / List Container */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: isPhone ? '10px' : '16px' }}>
                            {/* Desktop Table Header */}
                            {!isPhone && filteredLogs.length > 0 && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isCompact ? '2fr 1.6fr 1fr 1fr 1fr auto' : '2.2fr 1.6fr 1.1fr 1.1fr 1.1fr 1fr 200px',
                                    gap: '12px',
                                    padding: '8px 16px 10px 18px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: 'var(--textMuted)',
                                    borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)',
                                    marginBottom: '8px',
                                }}>
                                    <div>Drone / Asset</div>
                                    <div>Date & Time</div>
                                    <div>Duration</div>
                                    <div>Max Alt</div>
                                    <div>Distance</div>
                                    <div>Status</div>
                                    <div style={{ textAlign: 'right' }}>Actions</div>
                                </div>
                            )}

                            {/* Empty State */}
                            {filteredLogs.length === 0 && !loading && (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '60px 20px',
                                    color: 'var(--textMuted)',
                                    textAlign: 'center',
                                }}>
                                    <div style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)',
                                        border: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '14px',
                                        color: 'var(--textSecondary)',
                                    }}>
                                        <IconDrone size={26} />
                                    </div>
                                    <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--textPrimary)', marginBottom: '6px' }}>
                                        No flight logs found
                                    </div>
                                    <div style={{ fontSize: '13px', maxWidth: '360px', lineHeight: 1.5 }}>
                                        {searchQuery || statusFilter !== 'all' || effectiveSelectedDroneId
                                            ? 'No records match your active search and filter criteria. Try adjusting your filters.'
                                            : 'Flight telemetry snapshots are automatically recorded whenever an asset is armed and mission telemetry is received.'}
                                    </div>
                                    {(searchQuery || statusFilter !== 'all' || overrideDroneFilter) && (
                                        <button
                                            type="button"
                                            onClick={() => { setSearchQuery(''); setStatusFilter('all'); setOverrideDroneFilter(false); }}
                                            className="utms-toolbar-btn"
                                            style={{ marginTop: '16px' }}
                                        >
                                            Reset Filters
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Flight Log Rows */}
                            {filteredLogs.map(log => {
                                const isExpanded = expandedLogId === log._id;
                                const isGeneratingReport = reportLoadingId === log._id;
                                
                                // Status styling config
                                const statusCfg = {
                                    recording: { bg: 'color-mix(in srgb, var(--warning) 10%, transparent)', color: '#fbbf24', border: 'color-mix(in srgb, var(--warning) 30%, transparent)', dot: 'var(--warning)', pulse: true, label: 'In Flight' },
                                    completed: { bg: 'color-mix(in srgb, var(--success) 10%, transparent)', color: '#34d399', border: 'color-mix(in srgb, var(--success) 25%, transparent)', dot: 'var(--success)', pulse: false, label: 'Completed' },
                                    aborted:   { bg: 'color-mix(in srgb, var(--danger) 10%, transparent)',  color: '#f87171', border: 'color-mix(in srgb, var(--danger) 25%, transparent)',  dot: 'var(--danger)', pulse: false, label: 'Aborted' },
                                }[log.status] || { bg: 'color-mix(in srgb, var(--textPrimary) 5%, transparent)', color: 'var(--textMuted)', border: 'color-mix(in srgb, var(--textPrimary) 10%, transparent)', dot: '#888', pulse: false, label: log.status || 'Unknown' };

                                const startDate = log.startedAt ? new Date(log.startedAt) : null;

                                return (
                                    <div
                                        key={log._id}
                                        onClick={() => handleExpand(log._id)}
                                        className={`utms-flight-row ${isExpanded ? 'expanded' : ''}`}
                                    >
                                        {/* Status Accent Bar on Left */}
                                        <div style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: '3px',
                                            background: statusCfg.dot,
                                        }} />

                                        {/* Row Summary Grid */}
                                        <div style={{
                                            display: isPhone ? 'flex' : 'grid',
                                            flexDirection: isPhone ? 'column' : undefined,
                                            gridTemplateColumns: isCompact ? '2fr 1.6fr 1fr 1fr 1fr auto' : '2.2fr 1.6fr 1.1fr 1.1fr 1.1fr 1fr 200px',
                                            gap: isPhone ? '10px' : '12px',
                                            alignItems: isPhone ? 'stretch' : 'center',
                                            padding: isPhone ? '12px 14px 12px 16px' : '12px 16px 12px 18px',
                                            fontSize: '13px',
                                        }}>
                                            {/* Drone Identifier */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
                                                    border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)',
                                                    color: 'var(--accent)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <IconDrone size={17} />
                                                </div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontWeight: 700, color: 'var(--textPrimary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {log?.droneName || log?.droneId || 'Autonomous Vehicle'}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: 'var(--textMuted)', fontFamily: 'var(--font-label)', letterSpacing: '0.02em' }}>
                                                        ID: {log?.droneId ? (log.droneId.length > 14 ? log.droneId.substring(0, 12) + '…' : log.droneId) : 'N/A'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Timestamp */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--textPrimary)', fontSize: '12px', fontWeight: 500 }}>
                                                    <IconCalendar size={12} color="var(--textMuted)" />
                                                    <span>{startDate ? startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--textMuted)', fontSize: '11px' }}>
                                                    <IconClock size={11} color="var(--textMuted)" />
                                                    <span>{startDate ? startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</span>
                                                </div>
                                            </div>

                                            {/* Mobile stats row */}
                                            {isPhone && (
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                                    gap: '8px',
                                                    padding: '8px 10px',
                                                    borderRadius: '6px',
                                                    background: 'rgba(0, 0, 0, 0.25)',
                                                    margin: '2px 0',
                                                }}>
                                                    <div>
                                                        <div style={{ fontSize: '10px', color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duration</div>
                                                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--textPrimary)' }}>{formatDuration(log.startedAt, log.endedAt)}</div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '10px', color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Max Alt</div>
                                                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--textPrimary)' }}>{(log.maxAltitude || 0).toFixed(1)} m</div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '10px', color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Distance</div>
                                                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--textPrimary)' }}>{formatDistance(log.distance)}</div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Desktop Duration */}
                                            {!isPhone && (
                                                <div>
                                                    <div style={{ color: 'var(--textPrimary)', fontWeight: 600 }}>{formatDuration(log.startedAt, log.endedAt)}</div>
                                                    <div style={{ color: 'var(--textMuted)', fontSize: '11px' }}>air time</div>
                                                </div>
                                            )}

                                            {/* Desktop Max Altitude */}
                                            {!isPhone && (
                                                <div>
                                                    <div style={{ color: 'var(--textPrimary)', fontWeight: 600 }}>{(log.maxAltitude || 0).toFixed(1)} m</div>
                                                    <div style={{ color: 'var(--textMuted)', fontSize: '11px' }}>agl peak</div>
                                                </div>
                                            )}

                                            {/* Desktop Distance */}
                                            {!isPhone && (
                                                <div>
                                                    <div style={{ color: 'var(--textPrimary)', fontWeight: 600 }}>{formatDistance(log.distance)}</div>
                                                    <div style={{ color: 'var(--textMuted)', fontSize: '11px' }}>track dist</div>
                                                </div>
                                            )}

                                            {/* Status Badge */}
                                            <div>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    padding: '3px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    background: statusCfg.bg,
                                                    color: statusCfg.color,
                                                    border: `1px solid ${statusCfg.border}`,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    <span style={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        background: statusCfg.dot,
                                                        animation: statusCfg.pulse ? 'utms-pulse-dot 1.5s infinite' : 'none',
                                                    }} />
                                                    {statusCfg.label}
                                                </span>
                                            </div>

                                            {/* Action Buttons */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '6px',
                                                    alignItems: 'center',
                                                    justifyContent: isPhone ? 'stretch' : 'flex-end',
                                                    flexWrap: 'nowrap',
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleExport(log._id, e)}
                                                    className="utms-btn-action utms-btn-csv"
                                                    style={{ flex: isPhone ? 1 : 'none', justifyContent: 'center' }}
                                                    title="Download raw flight telemetry in CSV format"
                                                >
                                                    <IconDownload size={13} />
                                                    <span>CSV</span>
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={(e) => handleOpenReport(log._id, e)}
                                                    disabled={isGeneratingReport}
                                                    className="utms-btn-action utms-btn-report"
                                                    style={{ flex: isPhone ? 1 : 'none', justifyContent: 'center' }}
                                                    title="Generate Comprehensive Flight PDF Report"
                                                >
                                                    {isGeneratingReport ? (
                                                        <IconRefresh size={13} color="currentColor" spinning={true} />
                                                    ) : (
                                                        <IconDocument size={13} />
                                                    )}
                                                    <span>{isGeneratingReport ? 'Compiling...' : 'Report'}</span>
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleExpand(log._id); }}
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        color: isExpanded ? 'var(--accent)' : 'var(--textMuted)',
                                                        padding: '4px',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                    title={isExpanded ? 'Collapse snapshot telemetry' : 'Expand snapshot telemetry'}
                                                >
                                                    <IconChevron size={14} color="inherit" expanded={isExpanded} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expanded Detail Drawer */}
                                        {isExpanded && expandedLog && (
                                            <div style={{
                                                borderTop: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
                                                padding: '14px 18px',
                                                background: 'rgba(15, 17, 20, 0.95)',
                                                fontSize: '12px',
                                            }}>
                                                {/* Mini Metric Cards */}
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: isPhone ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                                                    gap: '10px',
                                                    marginBottom: '14px',
                                                }}>
                                                    <div style={{ background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)', border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)', borderRadius: '8px', padding: '8px 12px' }}>
                                                        <div style={{ color: 'var(--textMuted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Top Groundspeed</div>
                                                        <div style={{ color: 'var(--textPrimary)', fontSize: '14px', fontWeight: 700, marginTop: '2px' }}>
                                                            {(expandedLog.maxSpeed || 0).toFixed(1)} <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--textSecondary)' }}>m/s</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)', border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)', borderRadius: '8px', padding: '8px 12px' }}>
                                                        <div style={{ color: 'var(--textMuted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Blackbox Snapshots</div>
                                                        <div style={{ color: 'var(--textPrimary)', fontSize: '14px', fontWeight: 700, marginTop: '2px' }}>
                                                            {(expandedLog.snapshots || []).length} <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--textSecondary)' }}>points</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)', border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)', borderRadius: '8px', padding: '8px 12px' }}>
                                                        <div style={{ color: 'var(--textMuted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Armed Time</div>
                                                        <div style={{ color: 'var(--textPrimary)', fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>
                                                            {new Date(expandedLog.startedAt).toLocaleTimeString()}
                                                        </div>
                                                    </div>
                                                    <div style={{ background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)', border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)', borderRadius: '8px', padding: '8px 12px' }}>
                                                        <div style={{ color: 'var(--textMuted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Disarmed Time</div>
                                                        <div style={{ color: 'var(--textPrimary)', fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>
                                                            {expandedLog.endedAt ? new Date(expandedLog.endedAt).toLocaleTimeString() : 'In Progress'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Snapshot Telemetry Table */}
                                                {(expandedLog.snapshots || []).length > 0 ? (
                                                    <div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--textSecondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                Recent Telemetry Points (Last {Math.min(20, expandedLog.snapshots.length)})
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: 'var(--textMuted)' }}>
                                                                Showing 1 Hz high-precision telemetry stream
                                                            </div>
                                                        </div>

                                                        <div style={{ maxHeight: '220px', overflow: 'auto', borderRadius: '8px', border: '1px solid color-mix(in srgb, var(--textPrimary) 7%, transparent)', background: 'rgba(10, 11, 13, 0.6)' }}>
                                                            <table style={{ width: '100%', minWidth: '850px', borderCollapse: 'collapse', fontSize: '11px', fontFamily: 'var(--font-label)' }}>
                                                                <thead>
                                                                    <tr style={{ background: 'color-mix(in srgb, var(--textPrimary) 4%, transparent)', color: 'var(--textMuted)', borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)', position: 'sticky', top: 0, backdropFilter: 'blur(8px)', zIndex: 1 }}>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>TIME</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>LATITUDE</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>LONGITUDE</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>ALT (M)</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>HEADING</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>SPEED</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>CLIMB</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>PITCH / ROLL</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>THROTTLE</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>BATTERY</th>
                                                                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>MODE</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {expandedLog.snapshots.slice(-20).map((s, i) => (
                                                                        <tr key={i} style={{ borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 3%, transparent)', color: 'var(--textPrimary)' }}>
                                                                            <td style={{ padding: '5px 10px', color: 'var(--textSecondary)' }}>
                                                                                {s.t ? new Date(s.t).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                                                                            </td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(s.lat || 0).toFixed(6)}</td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(s.lon || 0).toFixed(6)}</td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right', color: 'var(--accent)', fontWeight: 600 }}>{(s.alt || 0).toFixed(1)}</td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right' }}>{Math.round(s.heading || 0)}°</td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right' }}>{(s.groundspeed || 0).toFixed(1)} m/s</td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right', color: (s.climbRate || 0) >= 0 ? '#34d399' : '#f87171' }}>
                                                                                {(s.climbRate || 0) >= 0 ? '+' : ''}{(s.climbRate || 0).toFixed(2)}
                                                                            </td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right', color: 'var(--textSecondary)' }}>
                                                                                {(s.pitch || 0).toFixed(1)}° / {(s.roll || 0).toFixed(1)}°
                                                                            </td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right' }}>{s.throttle || 0}%</td>
                                                                            <td style={{ padding: '5px 10px', textAlign: 'right', color: (s.battery_remaining || 100) < 25 ? 'var(--danger)' : '#34d399', fontWeight: 600 }}>
                                                                                {s.battery_remaining || 0}%
                                                                            </td>
                                                                            <td style={{ padding: '5px 10px' }}>
                                                                                <span style={{
                                                                                    padding: '1px 6px',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: '10px',
                                                                                    fontWeight: 600,
                                                                                    background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
                                                                                    color: 'var(--accent)',
                                                                                    border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)',
                                                                                }}>
                                                                                    {s.mode || 'N/A'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--textMuted)' }}>
                                                        No telemetry points were recorded for this flight session.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Comprehensive Flight Report Modal */}
            {reportLog && (
                <FlightReportModal
                    log={reportLog}
                    onClose={() => setReportLog(null)}
                />
            )}
        </div>
    );
}

export default LogsPage;
